import { Controller } from "@hotwired/stimulus"

// Keyboard-highlight ring for the active suggestion. ring-2 is the guaranteed
// token; ring-primary tints it (falls back to a currentColor ring if unbuilt).
const HIGHLIGHT = ["ring-2", "ring-primary"]

export default class extends Controller<HTMLElement> {
  static targets = ["modal", "input", "panel", "modifier", "loading"]
  static values = { suggestionsUrl: String }

  declare readonly modalTarget: HTMLDialogElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly panelTarget: HTMLElement
  declare readonly hasModifierTarget: boolean
  declare readonly modifierTarget: HTMLElement
  declare readonly loadingTarget: HTMLElement
  declare readonly suggestionsUrlValue: string

  private timeout?: number
  private selectedIndex = -1
  private boundShortcut = this.handleShortcut.bind(this)
  private boundReset = this.reset.bind(this)

  connect() {
    window.addEventListener("keydown", this.boundShortcut)
    document.addEventListener("turbo:before-cache", this.boundReset)
    this.renderModifier()
  }

  disconnect() {
    clearTimeout(this.timeout)
    window.removeEventListener("keydown", this.boundShortcut)
    document.removeEventListener("turbo:before-cache", this.boundReset)
  }

  open(event?: Event) {
    event?.preventDefault()
    if (this.modalTarget.open) {
      this.inputTarget.focus()
      return
    }

    this.modalTarget.showModal()
    this.inputTarget.focus()
    const caret = this.inputTarget.value.length
    this.inputTarget.setSelectionRange(caret, caret)
  }

  // The dialog fires `close` on backdrop click, Esc, or reset — drop the panel
  // so a later reopen starts clean (the input value is intentionally kept).
  onClose() {
    this.hidePanel()
  }

  // Close before Turbo caches the page — a dialog snapshotted open would restore
  // stuck open (same concern as the site popups).
  reset() {
    if (this.modalTarget.open) this.modalTarget.close()
  }

  query() {
    clearTimeout(this.timeout)
    const value = this.inputTarget.value.trim()

    if (value.length < 2) {
      this.hidePanel()
      return
    }

    // Setting the frame's src fetches and swaps the panel; Turbo cancels any
    // in-flight frame request when a newer src lands.
    this.timeout = window.setTimeout(() => {
      this.panelTarget.setAttribute(
        "src",
        `${this.suggestionsUrlValue}?s=${encodeURIComponent(value)}`,
      )
    }, 300)
  }

  // Hover-prefetch of suggestion links dispatches turbo:before-fetch-request /
  // turbo:fetch-request-error on the link, bubbling through the frame — only
  // the frame's own fetch (event.target === frame) should drive the UI.
  loading(event: Event) {
    if (event.target !== this.panelTarget) return
    this.loadingTarget.classList.remove("hidden")
  }

  rendered() {
    this.loadingTarget.classList.add("hidden")
    // The input may have been cleared while the frame request was in flight —
    // don't reveal stale results over a query the user has moved on from.
    if (this.inputTarget.value.trim().length < 2) return

    this.selectedIndex = -1
    this.showPanel()
  }

  failed(event: Event) {
    if (event.target !== this.panelTarget) return
    this.hidePanel()
  }

  keydown(event: KeyboardEvent) {
    if (this.panelHidden()) return

    const items = this.suggestionLinks()
    if (items.length === 0) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
        this.highlight(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1)
        this.highlight(items)
        break
      case "Enter":
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          event.preventDefault()
          items[this.selectedIndex].click()
        }
        break
    }
  }

  submit(event: Event) {
    if (this.inputTarget.value.trim().length === 0) event.preventDefault()
    this.hidePanel()
  }

  private handleShortcut(event: KeyboardEvent) {
    if (event.key?.toLowerCase() !== "k") return
    if (!(event.metaKey || event.ctrlKey)) return
    if (event.altKey || event.shiftKey) return

    event.preventDefault()
    this.open()
  }

  private highlight(items: HTMLElement[]) {
    items.forEach((item, index) => {
      HIGHLIGHT.forEach((token) => item.classList.toggle(token, index === this.selectedIndex))
    })
  }

  private suggestionLinks(): HTMLElement[] {
    return Array.from(this.panelTarget.querySelectorAll<HTMLElement>("[data-suggestion-link]"))
  }

  private showPanel() {
    this.panelTarget.classList.remove("hidden")
  }

  private hidePanel() {
    this.panelTarget.classList.add("hidden")
    this.selectedIndex = -1
    this.loadingTarget.classList.add("hidden")
  }

  private panelHidden(): boolean {
    return this.panelTarget.classList.contains("hidden")
  }

  private renderModifier() {
    if (!this.hasModifierTarget) return

    const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.platform)
    if (isMac) this.modifierTarget.textContent = "⌘"
  }
}
